import React, { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import { Close } from "@mui/icons-material";
import { useParams } from "react-router-dom";

import { TutredTextArea } from "@/components/elements/TutredTextArea/TutredTextArea";
import useFirebaseContext from "@/hooks/useFirebaseAuth";

interface CancelLessonDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const CancelLessonDialog: React.FC<CancelLessonDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const { authenticatedAxios } = useFirebaseContext();

  const { lessonId } = useParams();

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setLoading(false);
    }
  }, [isOpen]);

  const handleDialogClose = () => {
    if (loading) return;

    setReason("");
    onClose();
  };

  const handleCancelLesson = async () => {
    if (!lessonId) return;

    setLoading(true);
    try {
      await authenticatedAxios.post(`/lessons/cancel/${lessonId}`, {
        reason,
      });

      handleDialogClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="cancel-lesson-dialog-title"
      slotProps={{
        paper: {
          sx: {
            maxWidth: 440,
            px: 4,
            py: 4,
          },
        },
      }}
    >
      <IconButton
        aria-label="Close cancel lesson dialog"
        onClick={handleDialogClose}
        disabled={loading}
        sx={{
          position: "absolute",
          top: -24,
          right: -24,
          color: "common.white",
        }}
      >
        <Close />
      </IconButton>

      <Stack spacing={3}>
        <Stack spacing={1} alignItems="center">
          <Typography id="cancel-lesson-dialog-title" variant="h2Serif">
            Cancel lesson?
          </Typography>
          <Typography variant="text13L" align="center" sx={{ maxWidth: 320 }}>
            Add an optional reason before cancelling this lesson.
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <TutredTextArea
            // eslint-disable-next-line jsx-a11y/no-autofocus -- primary input when dialog opens
            autoFocus
            placeholder="Reason for cancellation (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            rows={2}
            color="secondary"
            fullWidth
          />
        </Stack>

        <Stack direction="row" gap={1} justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleDialogClose}
            disabled={loading}
          >
            Keep lesson
          </Button>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            loading={loading}
            onClick={handleCancelLesson}
          >
            Cancel lesson
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default CancelLessonDialog;
